import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  PhoneIcon,
  UserGroupIcon,
  ChartBarIcon,
  ClockIcon,
  SignalIcon,
  CheckCircleIcon,
  SparklesIcon,
  ArrowPathRoundedSquareIcon,
} from '@heroicons/react/24/outline';
import { Card, CardContent } from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import LiveCallsSection from '../../components/LiveCalls/LiveCallsSection';
import { getSseAccessToken } from '../../utils/sseAuthToken';

interface Agent {
  id: string;
  name: string;
  email: string;
  isOnline: boolean;
  isAvailable: boolean;
  wasOnlineToday?: boolean;
  dailyCounters?: {
    totalCallsDialed: number;
    answeredCalls: number;
    interested: number;
    followUps: number;
    appIn: number;
    appOut: number;
  };
  callStatus: 'in-call' | 'ringing' | null;
}

interface DashboardData {
  campaigns: Array<{ id: string; name: string; status: string }>;
  agents: Agent[];
  leadStatusCounts: Record<string, number>;
  stats: {
    totalCallsToday: number;
    answeredToday: number;
    avgDuration: number;
    connectedAvgDuration?: number;
    avgWaitToNextCallSeconds?: number;
    totalLeads: number;
    activeCalls: number;
  };
}

const STATUS_LABELS: Record<string, { label: string; color: string }> = {
  // Rollup "Answered" removed — it duplicated Hang Up when hangups dominated.
  interested: { label: 'Interested', color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400' },
  not_interested: { label: 'Not Interested', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' },
  follow_up: { label: 'Follow Up', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400' },
  no_answer: { label: 'No Answer', color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400' },
  busy: { label: 'Busy', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400' },
  failed: { label: 'Failed', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' },
  unreachable: { label: 'Unreachable', color: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400' },
  do_not_call: { label: 'Do Not Call', color: 'bg-red-200 text-red-900 dark:bg-red-900/40 dark:text-red-300' },
  out_of_business: { label: 'Out of Business', color: 'bg-gray-200 text-gray-900 dark:bg-gray-800/40 dark:text-gray-300' },
  wrong_number: { label: 'Wrong Number', color: 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-400' },
  unqualified: { label: 'Unqualified', color: 'bg-slate-100 text-slate-800 dark:bg-slate-900/30 dark:text-slate-400' },
  gatekeeper: { label: 'Gatekeeper', color: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-400' },
  app_in: { label: 'App In', color: 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-400' },
  app_out: { label: 'App Out', color: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400' },
  spanish: { label: 'Spanish', color: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-400' },
  hangup: { label: 'Hang Up', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400' },
  operator: { label: 'Operator', color: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-400' },
  voicemail: { label: 'Voicemail', color: 'bg-orange-200 text-orange-900 dark:bg-orange-900/40 dark:text-orange-300' },
};

function formatSeconds(sec: number): string {
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
}

function formatWaitTime(sec?: number): string {
  const safe = Math.max(Math.floor(sec || 0), 0);
  if (safe >= 3600) return `${Math.floor(safe / 3600)}h ${Math.floor((safe % 3600) / 60)}m`;
  if (safe >= 60) return `${Math.floor(safe / 60)}m ${safe % 60}s`;
  return `${safe}s`;
}

const LiveMonitorPage: React.FC = () => {
  const today = new Date().toISOString().slice(0, 10);
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [selectedAgent, setSelectedAgent] = useState('');
  const [fromDate, setFromDate] = useState(today);
  const [toDate, setToDate] = useState(today);
  const eventSourceRef = useRef<EventSource | null>(null);
  const reconnectTimerRef = useRef<number | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const closedRef = useRef(false);

  const streamFilters = useMemo(
    () => ({ selectedCampaign, selectedAgent, fromDate, toDate }),
    [selectedCampaign, selectedAgent, fromDate, toDate]
  );

  useEffect(() => {
    if (typeof window === 'undefined' || !('EventSource' in window)) return;

    closedRef.current = false;
    setLoading(true);

    const buildStreamUrl = (token: string) => {
      const baseUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';
      const fullUrl = baseUrl.startsWith('http')
        ? `${baseUrl}/admin/dialer-dashboard/stream`
        : `${window.location.origin}${baseUrl}/admin/dialer-dashboard/stream`;
      const url = new URL(fullUrl);
      url.searchParams.set('token', token);
      if (streamFilters.selectedCampaign) {
        url.searchParams.set('campaignId', streamFilters.selectedCampaign);
      }
      if (streamFilters.selectedAgent) {
        url.searchParams.set('agentId', streamFilters.selectedAgent);
      }
      if (streamFilters.fromDate) {
        url.searchParams.set('startDate', `${streamFilters.fromDate}T00:00:00.000Z`);
      }
      if (streamFilters.toDate) {
        url.searchParams.set('endDate', `${streamFilters.toDate}T23:59:59.999Z`);
      }
      return url.toString();
    };

    const cleanup = () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      eventSourceRef.current?.close();
      eventSourceRef.current = null;
    };

    const scheduleReconnect = () => {
      if (closedRef.current || reconnectTimerRef.current) return;
      if (document.visibilityState !== 'visible') return;
      reconnectAttemptsRef.current += 1;
      const delay = Math.min(30_000, 1000 * 2 ** (reconnectAttemptsRef.current - 1));
      reconnectTimerRef.current = window.setTimeout(() => {
        reconnectTimerRef.current = null;
        void connect();
      }, delay);
    };

    const connect = async () => {
      cleanup();
      if (closedRef.current) return;

      // Avoid reconnect storm if tab is hidden
      if (document.visibilityState !== 'visible') {
        scheduleReconnect();
        return;
      }

      try {
        const token = await getSseAccessToken();
        if (!token) {
          scheduleReconnect();
          return;
        }

        const es = new EventSource(buildStreamUrl(token));
        eventSourceRef.current = es;

        const handlePayload = (raw: string) => {
          try {
            const payload = JSON.parse(raw) as DashboardData;
            setData(payload);
          } catch {
            // ignore parse errors
          } finally {
            setLoading(false);
          }
        };

        es.addEventListener('dialer-dashboard', (event) => {
          const e = event as MessageEvent;
          handlePayload(e.data || '');
        });

        // Back-compat: if server ever sends default messages
        es.addEventListener('message', (event) => {
          const e = event as MessageEvent;
          if (e.data) handlePayload(e.data);
        });

        es.addEventListener('ping', () => {
          // keepalive, no-op
        });

        es.onopen = () => {
          reconnectAttemptsRef.current = 0;
        };

        es.onerror = () => {
          setLoading(false);
          es.close();
          eventSourceRef.current = null;
          scheduleReconnect();
        };
      } catch {
        scheduleReconnect();
      }
    };

    const handleVisibility = () => {
      if (closedRef.current) return;
      if (document.visibilityState === 'visible' && !eventSourceRef.current) {
        void connect();
      }
    };

    void connect();
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      closedRef.current = true;
      document.removeEventListener('visibilitychange', handleVisibility);
      cleanup();
    };
  }, [streamFilters]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-24">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const stats = data?.stats;
  const agents = data?.agents || [];
  const leadCounts = data?.leadStatusCounts || {};
  const campaigns = data?.campaigns || [];
  const includesToday = (() => {
    const now = new Date();
    const rangeStart = fromDate ? new Date(`${fromDate}T00:00:00`) : now;
    const rangeEnd = toDate ? new Date(`${toDate}T23:59:59.999`) : now;
    return now >= rangeStart && now <= rangeEnd;
  })();
  const agentsInRange = agents.filter((a) => {
    const hasRangeRecords =
      !!a.wasOnlineToday ||
      (a.dailyCounters?.totalCallsDialed ?? 0) > 0 ||
      (a.dailyCounters?.answeredCalls ?? 0) > 0 ||
      (a.dailyCounters?.interested ?? 0) > 0 ||
      (a.dailyCounters?.followUps ?? 0) > 0 ||
      (a.dailyCounters?.appIn ?? 0) > 0 ||
      (a.dailyCounters?.appOut ?? 0) > 0;
    return includesToday ? a.isOnline || hasRangeRecords : hasRangeRecords;
  });
  const onlineAgents = agentsInRange.filter(a => a.isOnline);
  const inCallAgents = agentsInRange.filter(a => a.callStatus === 'in-call');
  const visibleStatusTotal = Math.max(Number(leadCounts.total || 0), 1);
  const callsToAnswerRatio =
    (stats?.answeredToday ?? 0) > 0
      ? `1:${Math.max(Math.round((stats?.totalCallsToday ?? 0) / (stats?.answeredToday ?? 1)), 1)}`
      : '1:0';
  const metricCards = [
    {
      value: stats?.totalCallsToday ?? 0,
      label: 'Total Calls',
      subLabel: `${stats?.answeredToday ?? 0} connected`,
    },
    {
      value: callsToAnswerRatio,
      label: 'Call-to-Answer Ratio',
      subLabel: 'connections per calls',
    },
    {
      value: formatWaitTime(stats?.avgWaitToNextCallSeconds ?? 0),
      label: 'Avg Wait to Next Call',
      subLabel: 'between connected calls',
    },
    {
      value: formatSeconds(stats?.connectedAvgDuration ?? stats?.avgDuration ?? 0),
      label: 'Avg Connected Call Duration',
      subLabel: 'connected calls only',
    },
  ];
  const agentMetricItems = (agent: Agent) => [
    {
      key: 'dialed',
      label: 'Dialed',
      value: agent.dailyCounters?.totalCallsDialed ?? 0,
      icon: <PhoneIcon className="h-3.5 w-3.5" />,
      classes:
        'border-blue-200/70 dark:border-blue-800/60 bg-blue-50/80 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300',
    },
    {
      key: 'answered',
      label: 'Answered',
      value: agent.dailyCounters?.answeredCalls ?? 0,
      icon: <CheckCircleIcon className="h-3.5 w-3.5" />,
      classes:
        'border-emerald-200/70 dark:border-emerald-800/60 bg-emerald-50/80 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300',
    },
    {
      key: 'interested',
      label: 'Interested',
      value: agent.dailyCounters?.interested ?? 0,
      icon: <SparklesIcon className="h-3.5 w-3.5" />,
      classes:
        'border-violet-200/70 dark:border-violet-800/60 bg-violet-50/80 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300',
    },
    {
      key: 'follow-up',
      label: 'Follow Up',
      value: agent.dailyCounters?.followUps ?? 0,
      icon: <ArrowPathRoundedSquareIcon className="h-3.5 w-3.5" />,
      classes:
        'border-amber-200/70 dark:border-amber-800/60 bg-amber-50/80 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300',
    },
  ];

  return (
    <div className="p-6 space-y-6 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="space-y-3">
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <SignalIcon className="h-7 w-7 text-primary" />
          Live Monitor
        </h1>
        <div className="flex flex-wrap items-center gap-2">
          {campaigns.length > 1 && (
            <select
              value={selectedCampaign}
              onChange={(e) => setSelectedCampaign(e.target.value)}
              className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">All Campaigns</option>
              {campaigns.map(c => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          )}
          <select
            value={selectedAgent}
            onChange={(e) => setSelectedAgent(e.target.value)}
            className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          >
            <option value="">All Agents</option>
            {agents.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="rounded-lg border border-border bg-background px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          />
          <Button
            variant="outlined"
            size="sm"
            onClick={() => {
              setFromDate(today);
              setToDate(today);
            }}
          >
            Today
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
        <StatCard
          label="Active Calls"
          value={stats?.activeCalls ?? 0}
          icon={<PhoneIcon className="h-5 w-5" />}
          color="text-green-600 dark:text-green-400"
          bgColor="bg-green-100 dark:bg-green-900/30"
          pulse={!!stats?.activeCalls}
        />
        <StatCard
          label="Online Agents"
          value={onlineAgents.length}
          icon={<UserGroupIcon className="h-5 w-5" />}
          color="text-blue-600 dark:text-blue-400"
          bgColor="bg-blue-100 dark:bg-blue-900/30"
        />
        <StatCard
          label="Calls Today"
          value={stats?.totalCallsToday ?? 0}
          icon={<ChartBarIcon className="h-5 w-5" />}
          color="text-indigo-600 dark:text-indigo-400"
          bgColor="bg-indigo-100 dark:bg-indigo-900/30"
        />
        <StatCard
          label="Answered"
          value={stats?.answeredToday ?? 0}
          icon={<PhoneIcon className="h-5 w-5" />}
          color="text-emerald-600 dark:text-emerald-400"
          bgColor="bg-emerald-100 dark:bg-emerald-900/30"
        />
        <StatCard
          label="Avg Duration"
          value={formatSeconds(stats?.avgDuration ?? 0)}
          icon={<ClockIcon className="h-5 w-5" />}
          color="text-amber-600 dark:text-amber-400"
          bgColor="bg-amber-100 dark:bg-amber-900/30"
        />
      </div>

      {/* Agents Panel */}
      <Card className="card-elevated">
          <div className="px-5 py-4 border-b border-border bg-muted/50">
            <h2 className="text-base font-semibold text-foreground flex items-center gap-2">
              <UserGroupIcon className="h-5 w-5 text-primary" />
              Agents ({agentsInRange.length})
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              {onlineAgents.length} online &middot; {inCallAgents.length} on call
            </p>
          </div>
          <CardContent className="p-5">
            {agentsInRange.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                No agents found for selected date range
              </div>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-border/70">
                <table className="min-w-full text-sm">
                  <thead className="bg-muted/40 border-b border-border/60">
                    <tr>
                      <th className="px-4 py-3 text-left font-semibold text-foreground">Agent</th>
                      <th className="px-3 py-3 text-center font-semibold text-foreground">Status</th>
                      <th className="px-3 py-3 text-center font-semibold text-blue-700 dark:text-blue-300">Dialed</th>
                      <th className="px-3 py-3 text-center font-semibold text-emerald-700 dark:text-emerald-300">Answered</th>
                      <th className="px-3 py-3 text-center font-semibold text-violet-700 dark:text-violet-300">Interested</th>
                      <th className="px-3 py-3 text-center font-semibold text-amber-700 dark:text-amber-300">Follow Up</th>
                      <th className="px-3 py-3 text-center font-semibold text-indigo-700 dark:text-indigo-300">App Out</th>
                      <th className="px-3 py-3 text-center font-semibold text-sky-700 dark:text-sky-300">App In</th>
                    </tr>
                  </thead>
                  <tbody>
                {agentsInRange
                  .sort((a, b) => {
                    // in-call first, then online, then offline
                    const aPriority = a.callStatus ? 0 : a.isOnline ? 1 : 2;
                    const bPriority = b.callStatus ? 0 : b.isOnline ? 1 : 2;
                    return aPriority - bPriority;
                  })
                  .map(agent => (
                    <tr key={agent.id} className="border-b border-border/50 last:border-b-0 hover:bg-accent/20 transition-colors">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                      <div className="relative">
                        <div className="h-10 w-10 rounded-full bg-primary/10 ring-1 ring-primary/20 flex items-center justify-center text-sm font-semibold text-primary">
                          {(agent.name || 'A').charAt(0).toUpperCase()}
                        </div>
                        <span
                          className={`absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-background ${
                            agent.callStatus === 'in-call'
                              ? 'bg-green-500 animate-pulse'
                              : agent.callStatus === 'ringing'
                              ? 'bg-yellow-500 animate-pulse'
                              : agent.isOnline
                              ? 'bg-green-500'
                              : 'bg-gray-400'
                          }`}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm md:text-[15px] font-semibold text-foreground truncate">{agent.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{agent.email}</p>
                      </div>
                        </div>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span
                        className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                          agent.callStatus === 'in-call'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                            : agent.callStatus === 'ringing'
                            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                            : agent.isOnline
                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                            : 'bg-gray-100 text-gray-600 dark:bg-gray-800/30 dark:text-gray-400'
                        }`}
                      >
                        {agent.callStatus === 'in-call'
                          ? 'On Call'
                          : agent.callStatus === 'ringing'
                          ? 'Ringing'
                          : agent.isOnline
                          ? 'Online'
                          : 'Offline (active today)'}
                      </span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className="inline-flex min-w-9 justify-center rounded-md border border-blue-200/70 dark:border-blue-800/60 bg-blue-50/80 dark:bg-blue-900/20 px-2 py-0.5 font-semibold text-blue-700 dark:text-blue-300">
                          {agent.dailyCounters?.totalCallsDialed ?? 0}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className="inline-flex min-w-9 justify-center rounded-md border border-emerald-200/70 dark:border-emerald-800/60 bg-emerald-50/80 dark:bg-emerald-900/20 px-2 py-0.5 font-semibold text-emerald-700 dark:text-emerald-300">
                          {agent.dailyCounters?.answeredCalls ?? 0}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className="inline-flex min-w-9 justify-center rounded-md border border-violet-200/70 dark:border-violet-800/60 bg-violet-50/80 dark:bg-violet-900/20 px-2 py-0.5 font-semibold text-violet-700 dark:text-violet-300">
                          {agent.dailyCounters?.interested ?? 0}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className="inline-flex min-w-9 justify-center rounded-md border border-amber-200/70 dark:border-amber-800/60 bg-amber-50/80 dark:bg-amber-900/20 px-2 py-0.5 font-semibold text-amber-700 dark:text-amber-300">
                          {agent.dailyCounters?.followUps ?? 0}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className="inline-flex min-w-9 justify-center rounded-md border border-indigo-200/70 dark:border-indigo-800/60 bg-indigo-50/80 dark:bg-indigo-900/20 px-2 py-0.5 font-semibold text-indigo-700 dark:text-indigo-300">
                          {agent.dailyCounters?.appOut ?? 0}
                        </span>
                      </td>
                      <td className="px-3 py-3 text-center">
                        <span className="inline-flex min-w-9 justify-center rounded-md border border-sky-200/70 dark:border-sky-800/60 bg-sky-50/80 dark:bg-sky-900/20 px-2 py-0.5 font-semibold text-sky-700 dark:text-sky-300">
                          {agent.dailyCounters?.appIn ?? 0}
                        </span>
                      </td>
                    </tr>
                  ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
      </Card>

      {/* Lead Status Breakdown */}
      <Card className="card-elevated">
          <div className="px-5 py-4 border-b border-border bg-muted/50">
            <h2 className="text-base font-semibold text-foreground flex items-center gap-2">
              <ChartBarIcon className="h-5 w-5 text-primary" />
              Lead Status Breakdown
            </h2>
            <p className="text-xs text-muted-foreground mt-0.5">
              Real-time outcomes for monitored calls
            </p>
          </div>
          <CardContent className="p-5">
            <div className="grid grid-cols-2 gap-3 mb-5">
              {metricCards.map((metric) => (
                <div key={metric.label} className="rounded-xl border border-border/70 bg-muted/20 px-4 py-3">
                  <div className="text-xl font-bold text-foreground leading-tight">{metric.value}</div>
                  <div className="text-xs font-medium text-muted-foreground mt-1">{metric.label}</div>
                  <div className="text-[11px] text-muted-foreground/80">{metric.subLabel}</div>
                </div>
              ))}
            </div>
            {Object.keys(leadCounts).length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                No leads found
              </div>
            ) : (
              <>
                {/* Visual bar chart */}
                <div className="space-y-3">
                  {Object.entries(STATUS_LABELS).map(([key, meta]) => {
                    const count = leadCounts[key] ?? 0;
                    const total = visibleStatusTotal;
                    const pct = Math.round((count / total) * 100);
                    return (
                      <div key={key} className="flex items-center gap-3">
                        <div className="w-28 flex-shrink-0">
                          <span className={`inline-flex text-xs px-2 py-0.5 rounded-full font-medium ${meta.color}`}>
                            {meta.label}
                          </span>
                        </div>
                        <div className="flex-1 h-6 bg-muted rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all duration-500 ${meta.color.split(' ')[0]}`}
                            style={{ width: `${Math.max(pct, 1)}%` }}
                          />
                        </div>
                        <div className="w-16 text-right">
                          <span className="text-sm font-semibold text-foreground">{count}</span>
                          <span className="text-xs text-muted-foreground ml-1">({pct}%)</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </CardContent>
      </Card>

      {/* Live Calls Section (with Listen & Whisper) */}
      <LiveCallsSection />
    </div>
  );

};

// ─── Stat Card Component ─────

interface StatCardProps {
  label: string;
  value: number | string;
  icon: React.ReactNode;
  color: string;
  bgColor: string;
  pulse?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({ label, value, icon, color, bgColor, pulse }) => (
  <Card className="card-elevated">
    <CardContent className="p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2.5 rounded-xl ${bgColor}`}>
          <span className={color}>{icon}</span>
        </div>
        <div>
          <p className="text-xs text-muted-foreground font-medium">{label}</p>
          <p className={`text-xl font-bold text-foreground ${pulse ? 'animate-pulse' : ''}`}>
            {value}
          </p>
        </div>
      </div>
    </CardContent>
  </Card>
);

export default LiveMonitorPage;
